import { useEffect, useState } from "react";

import type { NextPage } from "next";
import Head from "next/head";
import Script from "next/script";

import classNames from "classnames/bind";
import Breadcrumbs from "common/blocks/Breadcrumbs";
import RecentlyViewed from "common/blocks/RecentlyViewed";
import RichText from "common/blocks/RichText";
import Layout from "common/layout/Layout";
import useStructuredDataContext from "common/providers/structuredData/useStructuredDataContext";
import VehicleNotFound from "features/errors/vehicleNotFound/templates";
import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import { ExtrasRow } from "features/vehicleDetails/components/ExtrasRow";
import { FinanceRow } from "features/vehicleDetails/components/FinanceRow";
import ImageViewer, { ImageViewerMode } from "features/vehicleDetails/components/ImageViewer";
import { QualityRow } from "features/vehicleDetails/components/QualityRow";
import RecommendedVehicles from "features/vehicleDetails/components/RecommendedVehicles";
import SavingsBanner from "features/vehicleDetails/components/SavingsBanner";
import {
    VehicleDetails,
    VehicleDetailsProps,
} from "features/vehicleDetails/components/VehicleDetails";
import {
    VehicleFigures,
    VehicleFiguresProps,
} from "features/vehicleDetails/components/VehicleFigures";
import VehicleOverview from "features/vehicleDetails/components/VehicleOverview";
import { ComingSoonAlertProvider } from "features/vehicleDetails/components/VehicleOverview/context";
import WidgetsRow, { WidgetsRowProps } from "features/vehicleDetails/components/WidgetsRow";
import { comingSoonVehicleAlertModal } from "features/vehicleDetails/modals";
import {
    FeaturesSpecsModalProps,
    FeaturesSpecsSelectedTab,
} from "features/vehicleDetails/modals/FeaturesSpecs";
import PDPProviders from "features/vehicleDetails/providers";
import { EventNames, useSub } from "hooks/usePublishSubscribe";
import type { PDPProps } from "pages/vehicle-details/[...makemodel]";
import type { PromoKey } from "resolvers/pages/pdpPageResolver";
import { VehicleService } from "services/vehicle/vehicle";
import generateGtmEcommerceEntryString from "utils/generateGtmEcommerceEntryString";

import { PDPPromoArea } from "../../components/PDPPromoArea";
import styles from "./pdp.module.scss";

const cx = classNames.bind(styles);

const PDP: NextPage<PDPProps & { errorCode?: number }> = ({
    baseData,
    vehicle,
    page,
    meta,
    breadcrumbs,
    recentlyViewedStockBookNumbers,
    errorCode,
    vehicleCardSettings,
    dynamicSavingsSidebar,
    fourTenErrorContent,
    widgetRowcontent,
}) => {
    const { useShortlist } = useFavouritesContext();
    const shortlist = useShortlist();
    const isSaved = shortlist?.stockNumbers?.includes(vehicle.StockBookNumber);

    const { createPdpStructuredData, pdpContent } = useStructuredDataContext();

    const showPromos = true;

    useEffect(() => {
        if (!pdpContent) {
            createPdpStructuredData({
                vehicle,
                formattedMake: page.formattedMakeModel.make,
                formattedModel: page.formattedMakeModel.model,
            });
        }
    }, [
        createPdpStructuredData,
        page.formattedMakeModel.make,
        page.formattedMakeModel.model,
        pdpContent,
        vehicle,
    ]);

    //Consume the event thats fired after vehicle image update has completed
    const setImageViewerOpenWithPubSub = useSub(
        EventNames.ImageViewerOpenCompletionEvent,
        (data) => {
            const { isOpen } = data;

            if (isOpen) {
                window?.imichatwidget?.hide?.();
            } else {
                window?.imichatwidget?.show?.();
            }
            return;
        }
    );

    const handleOpenFeaturesSpecsModal = (
        selectedRow?: string,
        selectedTab?: FeaturesSpecsSelectedTab
    ) => {
        let vehicleFigureProps: VehicleFiguresProps["components"] | undefined = undefined;
        let vehicleDetailsProps: VehicleDetailsProps["components"] | undefined = undefined;

        page.content.forEach((section) => {
            if (!!section?.vehicleDetails) vehicleDetailsProps = section.vehicleDetails;
            if (!!section?.vehicleFigures) vehicleFigureProps = section.vehicleFigures;
        });

        if (!vehicleFigureProps || !vehicleDetailsProps) return;

        const featuresSpecsModalProps: FeaturesSpecsModalProps["params"] = {
            selectedRow,
            selectedTab,
            vehicleDetailsProps,
            vehicleFigureProps,
        };

        return featuresSpecsModalProps;
    };

    const googlePlaceId = page.allStoresDetails?.find(
        (store) => store?.siteId === vehicle?.AllocatedLocation?.Id
    )?.googleMaps?.placeId;

    const gtmDatalayerContents = generateGtmEcommerceEntryString({
        event: "view_item",
        itemListId: "pdp-page",
        vehicle,
        googlePlaceId,
    });

    if (errorCode === 410) {
        return (
            <VehicleNotFound
                baseData={baseData}
                page={page}
                breadcrumbs={breadcrumbs}
                vehicleCardSettings={vehicleCardSettings}
                fourTenErrorContent={fourTenErrorContent}
                vehicle={vehicle}
            />
        );
    }

    const processImageArrayToIncludeImageSizes = (vehicleImage: VehicleService.MarketingImage) => {
        vehicleImage.SizedImages?.sort((a, b) => b.Format.localeCompare(a.Format));
    };
    vehicle.Imagery.MarketingImages.forEach((image) => {
        processImageArrayToIncludeImageSizes(image);
    });

    const comingSoon: WidgetsRowProps["comingSoon"] = vehicle.ComingSoonDetails?.IsComingSoon
        ? {
              averageDaysToPrep: vehicle.ComingSoonDetails.AverageDaysToPrep,
              dateBecameComingSoon: new Date(vehicle.ComingSoonDetails.DateBecameComingSoon),
              openSidebarCallback: () =>
                  comingSoonVehicleAlertModal(vehicle, widgetRowcontent.alertSidePanel),
          }
        : undefined;

    return (
        <ComingSoonAlertProvider>
            <PDPProviders
                closestStores={page.closestStores}
                allStoresDetails={page.allStoresDetails}
                vehicle={vehicle}
            >
                <Layout baseData={baseData} meta={meta} overideShowRotatingBanners>
                    <Head>
                        <meta name="robots" content="noindex, follow" />
                    </Head>
                    <Script id="pdp-datalayer-push">{`
                        window.dataLayer = window.dataLayer || [];
                        dataLayer.push(${gtmDatalayerContents});
                    `}</Script>
                    <Script
                        async
                        defer
                        src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                    />
                    <ImageViewer media={vehicle.Imagery} />
                    <section id="breadcrumbs" className={cx("container", "breadcrumbs")}>
                        <div className={cx("row")}>
                            <Breadcrumbs breadcrumbs={breadcrumbs} />
                        </div>
                    </section>
                    {page.content.map((section, index) => {
                        switch (true) {
                            case !!section?.vehicleOverview: {
                                return (
                                    <section
                                        key="overview"
                                        id="overview"
                                        className={cx("container")}
                                    >
                                        <div className={cx("row")}>
                                            <VehicleOverview
                                                formattedMake={page.formattedMakeModel.make}
                                                formattedModel={page.formattedMakeModel.model}
                                                formattedVariant={page.formattedMakeModel.variant}
                                                vehicle={vehicle}
                                                components={section.vehicleOverview}
                                                showPromo={showPromos}
                                                googlePlaceId={googlePlaceId}
                                                isSaved={isSaved}
                                                vehicleCardSettings={vehicleCardSettings}
                                                sideBarContent={widgetRowcontent.alertSidePanel}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.extrasRow: {
                                return (
                                    <section
                                        key="extras"
                                        id="extras"
                                        className={cx("container", "greyBackground")}
                                    >
                                        <div className={cx("row")}>
                                            <ExtrasRow components={section.extrasRow} />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.widgetsRow: {
                                return (
                                    <section
                                        key="widgets"
                                        id="widgets"
                                        className={cx("container", "noXs")}
                                        style={{
                                            backgroundColor: widgetRowcontent.backgroundColour,
                                        }}
                                    >
                                        <div className={cx("row")}>
                                            <WidgetsRow
                                                components={section.widgetsRow}
                                                comingSoon={comingSoon}
                                                content={widgetRowcontent}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.financeRow: {
                                return (
                                    <section
                                        key="finance-calculator"
                                        id="finance-calculator"
                                        className={cx("container")}
                                    >
                                        <div className={cx("row")}>
                                            <FinanceRow
                                                components={section.financeRow}
                                                isComingSoon={
                                                    vehicle.ComingSoonDetails?.IsComingSoon
                                                }
                                                externalIds={vehicle.ExternalIds}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.vehicleDetails: {
                                return (
                                    <section key="details" id="details" className={cx("container")}>
                                        <div className={cx("row")}>
                                            <VehicleDetails
                                                handleOpenFeaturesSpecsModal={
                                                    handleOpenFeaturesSpecsModal
                                                }
                                                components={section.vehicleDetails}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.qualityRow: {
                                return (
                                    <section
                                        key="quality-standard"
                                        id="quality-standard"
                                        className={cx("container")}
                                    >
                                        <div className={cx("row")}>
                                            <QualityRow components={section.qualityRow} />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.vehicleFigures: {
                                return (
                                    <section
                                        key="figures"
                                        id="figures"
                                        className={cx("container", "greyBackground")}
                                    >
                                        <div className={cx("row")}>
                                            <VehicleFigures
                                                handleOpenFeaturesSpecsModal={
                                                    handleOpenFeaturesSpecsModal
                                                }
                                                components={section.vehicleFigures}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                            case !!section?.[`promo${index}` as PromoKey]: {
                                const { body, ...rest } = section?.[`promo${index}` as PromoKey];
                                return (
                                    <PDPPromoArea
                                        key={`promo${index}`}
                                        id={`promo${index}`}
                                        fullWidth
                                        {...rest}
                                    >
                                        <RichText className={cx("promoRichText")} {...body} />
                                    </PDPPromoArea>
                                );
                            }

                            case !!section?.recommendedVehicles: {
                                return (
                                    <section key="recommended-vehicles" id="recommended-vehicles">
                                        <RecommendedVehicles
                                            stockBookNumber={vehicle.StockBookNumber}
                                            shortlistedStocks={shortlist?.stockNumbers ?? []}
                                            components={section?.recommendedVehicles}
                                            allStoresDetails={page.allStoresDetails}
                                            vehicleCardSettings={vehicleCardSettings}
                                            vehicleDisplaySettings={page.vehicleDisplaySettings}
                                        />
                                    </section>
                                );
                            }

                            case !!section?.recentlyViewed: {
                                return (
                                    <section
                                        key="recently-viewed"
                                        id="recently-viewed"
                                        className={cx("container")}
                                    >
                                        <div className={cx("row", "recentlyViewedRow")}>
                                            <RecentlyViewed
                                                currentPdpVehicleStockNumber={
                                                    vehicle.StockBookNumber
                                                }
                                                showBorderLine={false}
                                                stockBookNumbers={recentlyViewedStockBookNumbers}
                                                vehicleCardSettings={vehicleCardSettings}
                                                components={section?.recentlyViewed}
                                                vehicleDisplaySettings={page.vehicleDisplaySettings}
                                            />
                                        </div>
                                    </section>
                                );
                            }

                            case !!section?.listSavingsBanner: {
                                const basicPrice =
                                    vehicle?.CurrentPrice?.SalePrice?.BasicPrice ?? 0;
                                const vatAmount = vehicle?.CurrentPrice?.SalePrice?.VATAmount ?? 0;

                                const vehicleTotalPrice = vehicle.Taxonomy.IsCommercial
                                    ? basicPrice
                                    : basicPrice + vatAmount;

                                const originalRetailPriceBasic =
                                    vehicle?.OriginalRetailPrice?.BasicPrice ?? 0;
                                const originalRetailPriceVat =
                                    vehicle?.OriginalRetailPrice?.VATAmount ?? 0;

                                const listPriceValue = vehicle.Taxonomy.IsCommercial
                                    ? originalRetailPriceBasic
                                    : originalRetailPriceBasic + originalRetailPriceVat;
                                let savingsValue = listPriceValue - vehicleTotalPrice;
                                savingsValue = Math.round(savingsValue);

                                return (
                                    <section
                                        key="list-savings-banner"
                                        id="list-savings-banner"
                                        className={cx("container", "solitudeBackground")}
                                    >
                                        <div className={cx("listSavingsBannerBanner")}>
                                            <SavingsBanner
                                                components={section.listSavingsBanner}
                                                vehicle={vehicle}
                                                formattedMake={page.formattedMakeModel.make}
                                                formattedModel={page.formattedMakeModel.model}
                                                formattedVariant={page.formattedMakeModel.variant}
                                                dynamicSavingsSidebar={dynamicSavingsSidebar}
                                            />
                                        </div>
                                    </section>
                                );
                            }
                        }
                    })}
                </Layout>
            </PDPProviders>
        </ComingSoonAlertProvider>
    );
};

export default PDP;
